import {TextureType} from "@cm/material-nodes"
import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {JobNodes} from "@cm/job-nodes/job-nodes"
import type {Format, ImageMapInfo, InputMapsInfo, InputMapsNodes, NormalY} from "#material-maps-exporter/core/types"
import {IMG_FORMAT_TO_ENCODER_FN, materialMapsExporterLogger, PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT} from "#material-maps-exporter/core/types"
import {applyImageTransform, isSupportedImageTransformNode} from "#material-maps-exporter/core/transforms"

export function imageProcessingGraphForInputMaps(mapsInfo: InputMapsInfo): InputMapsNodes {
    const mapsNodes: InputMapsNodes = {}

    // TODO: move into backend
    for (const type of Object.keys(mapsInfo)) {
        const mapInfo = mapsInfo[type]
        let image = ((): Nodes.ImageNode => {
            if (mapInfo.data.type === "ShaderNodeRGB") {
                const color = mapInfo.data.shaderNodeRGB.parameters.settings.color
                if (!color) throw Error(`ShaderNodeRGB has no color parameter`)
                return Nodes.createImage(1, 1, "float32", "linear", [color.r, color.g, color.b])
            } else {
                const dataObjRef = JobNodes.dataObjectReference(mapInfo.data.dataObjectId)
                const decodedImage = Nodes.decode(Nodes.externalData(dataObjRef, "encodedData"))
                return decodedImage
            }
        })()

        image = mapInfo.transformations.reduce((acc, transformation) => {
            if (!isSupportedImageTransformNode(transformation)) throw Error(`Unsupported image transformation: ${transformation.nodeClass}`)
            if (transformation.parameters.ui?.disabled) return acc
            return applyImageTransform(transformation, acc)
        }, image)

        //All potential further computations on the maps should be done in floating linear color space
        image = Nodes.convert(image, "float32", "RGBA", false)

        const channelLayout = PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT[type]
        // Some of the manually modified maps are not necessarily stored with L/RGB layout.
        // Explicitly convert image data into format with the expected layout to avoid disambiguity
        if (channelLayout === "L") {
            mapsNodes[type] = Nodes.extractChannel(image, 0)
        } else if (channelLayout === "RGB") {
            mapsNodes[type] = Nodes.combineChannels([Nodes.extractChannel(image, 0), Nodes.extractChannel(image, 1), Nodes.extractChannel(image, 2)], "RGB")
        } else {
            throw Error(`Unsupported channel layout for texture type: ${type}`)
        }
    }

    return mapsNodes
}

export function mix(a: Nodes.ImageNode, b: Nodes.ImageNode | number, m: Nodes.ImageNode | number): Nodes.Math {
    const b_ = typeof b === "number" ? Nodes.math("constLike", a, b) : b
    const m_ = typeof m === "number" ? Nodes.math("constLike", a, m) : m
    return Nodes.math("-", Nodes.math("*", b_, m), Nodes.math("*", a, Nodes.math("-", m_, 1)))
}

export function encodeConvertedNode(
    imageNode: Nodes.ImageNode,
    format: Format,
    dpi: number | undefined,
    colorData = false,
    singleChannel = true,
): Nodes.Encode {
    let dataType: Nodes.Convert["dataType"]
    let options: {[key: string]: unknown} = {}
    let forceLinear: boolean

    if (format === "exr") {
        dataType = "float32"
        options = {bitDepth: 16}
        forceLinear = true
    } else if (format === "tiff") {
        dataType = "uint8"
        forceLinear = false
    } else if (format === "png") {
        dataType = "uint8"
        forceLinear = false
    } else if (format === "jpeg") {
        dataType = "uint8"
        forceLinear = false
    } else {
        throw Error(`Unsupported format: ${format}`)
    }

    const sRGB = colorData && !forceLinear
    let output: Nodes.ImageNode
    if (singleChannel) {
        output = Nodes.convert(imageNode, dataType, "L", sRGB)
    } else {
        output = Nodes.combineChannels(
            [
                Nodes.convert(Nodes.extractChannel(imageNode, 0), dataType, "L", sRGB),
                Nodes.convert(Nodes.extractChannel(imageNode, 1), dataType, "L", sRGB),
                Nodes.convert(Nodes.extractChannel(imageNode, 2), dataType, "L", sRGB),
            ],
            "RGB",
        )
    }

    return Nodes.encode(Nodes.setDpi(output, dpi), IMG_FORMAT_TO_ENCODER_FN[format], options)
}

export function provideFallbackMap(
    fallbackResX: number,
    fallbackResY: number,
    colorSpace: "linear" | "sRGB",
    fallbackColor: Nodes.RGBColor | number,
): Nodes.ImageNode {
    return Nodes.createImage(fallbackResX, fallbackResY, "float32", colorSpace, fallbackColor)
}

export function getMapNodeOrProvideFallback(
    mapsNodes: InputMapsNodes,
    fallbackResX: number,
    fallbackResY: number,
    type: string,
    fallbackColor: Nodes.RGBColor | number,
    colorSpace: "linear" | "sRGB",
): Nodes.ImageNode {
    if (mapsNodes[type]) return mapsNodes[type]
    materialMapsExporterLogger?.debug(
        `Generating fallback map for ${type} with resolution ${fallbackResX}x${fallbackResY}, color ${fallbackColor} and ${colorSpace} color space`,
    )

    return provideFallbackMap(fallbackResX, fallbackResY, colorSpace, fallbackColor)
}

// NOTE: this is assuming that input rotation and strength specified in left-handed CS.
// Output computed in right-handed CS (matching vector representation of anisotropy on the platform)
// (This is the reason for the y-axis flip)
export function anisotropyRotationAndStrengthToVector(rotation: Nodes.ImageNode, strength: Nodes.ImageNode, valZ = 0.5): Nodes.ImageNode {
    const mag = Nodes.math("*", Nodes.math("-", strength, 0.5), 2.0)
    const rot = Nodes.math("*", rotation, 2 * 2 * Math.PI)
    let vecX = Nodes.math("*", Nodes.math("cos", rot), mag)
    let vecY = Nodes.math("*", Nodes.math("sin", rot), Nodes.math("*", mag, -1))
    vecX = Nodes.math("+", Nodes.math("*", vecX, 0.5), 0.5)
    vecY = Nodes.math("+", Nodes.math("*", vecY, 0.5), 0.5)
    const vecZ = Nodes.math("constLike", mag, valZ)

    return Nodes.combineChannels([vecX, vecY, vecZ], "RGB")
}

// NOTE: exact inverse of anisotropyRotationAndStrengthToVector, assuming input vector is in right-handed CS and output rotation and strength are in left-handed CS.
export function anisotropyVectorToRotationAndStrength(anisoVec: Nodes.ImageNode): {
    rotation: Nodes.ImageNode
    strength: Nodes.ImageNode
} {
    const vecX = Nodes.math("*", Nodes.math("-", Nodes.extractChannel(anisoVec, 0), 0.5), 2.0)
    const vecY = Nodes.math("*", Nodes.math("-", Nodes.extractChannel(anisoVec, 1), 0.5), 2.0)
    let rot = Nodes.math("/", Nodes.math("atan2", Nodes.math("*", vecY, -1), vecX), 2 * 2 * Math.PI)

    rot = Nodes.math("+", rot, Nodes.math("*", Nodes.math("<", rot, 0.0), 0.5))
    rot = Nodes.math("+", rot, Nodes.math("*", Nodes.math("<", rot, 0.0), 0.5))
    rot = Nodes.math("-", rot, Nodes.math("*", Nodes.math(">", rot, 0.5), 0.5))
    rot = Nodes.math("-", rot, Nodes.math("*", Nodes.math(">", rot, 0.5), 0.5))
    const strength = Nodes.math("sqrt", Nodes.math("+", Nodes.math("square", vecX), Nodes.math("square", vecY)))

    return {rotation: rot, strength: strength}
}

export function getVectorAnisotropy(mapsNodes: InputMapsNodes, fallbackResX: number, fallbackResY: number): Nodes.ImageNode {
    const types = Object.keys(mapsNodes)
    if (types.includes(TextureType.AnisotropyRotation) && types.includes(TextureType.AnisotropyStrength)) {
        return anisotropyRotationAndStrengthToVector(mapsNodes[TextureType.AnisotropyRotation], mapsNodes[TextureType.AnisotropyStrength])
    } else if (types.includes(TextureType.Anisotropy)) {
        return mapsNodes[TextureType.Anisotropy]
    } else {
        return getMapNodeOrProvideFallback(mapsNodes, fallbackResX, fallbackResY, TextureType.Anisotropy, [0.5, 0.5, 0], "linear")
    }
}

export function getImageMapInfos(mapsInfo: InputMapsInfo): ImageMapInfo[] {
    const imageMapsInfo: ImageMapInfo[] = []
    for (const textureType of Object.keys(mapsInfo)) {
        const info = mapsInfo[textureType]
        if (info.data.type === "DataObject") {
            const {width, height, physicalWidth, physicalHeight, displacementScale} = info.data
            imageMapsInfo.push({width, height, physicalWidth, physicalHeight, textureType, displacementScale})
        }
    }
    return imageMapsInfo
}

export function convertNormal(normal: Nodes.ImageNode, option: NormalY, def: Exclude<NormalY, "default">): Nodes.ImageNode {
    const normalY = option === "default" || option === undefined ? def : option

    switch (normalY) {
        case "y+down":
            return normal
        case "y+up": {
            const chX = Nodes.extractChannel(normal, 0)
            const chY = Nodes.math("+", Nodes.math("*", Nodes.extractChannel(normal, 1), -1), 1)
            const chZ = Nodes.extractChannel(normal, 2)
            return Nodes.combineChannels([chX, chY, chZ], "RGB")
        }
        default:
            throw Error(`Unsupported normal Y option: ${option}`)
    }
}

function convertTransmissionToMask(transmission: Nodes.ImageNode, transmissionMin: number, transmissionMax: number): Nodes.ImageNode {
    // transmission rgb -> v (brightness) component of transmission hsv
    const rTransmission = Nodes.extractChannel(transmission, 0)
    const gTransmission = Nodes.extractChannel(transmission, 1)
    const bTransmission = Nodes.extractChannel(transmission, 2)
    const vTransmission = Nodes.math("max", rTransmission, Nodes.math("max", gTransmission, bTransmission))

    // rescale, invert, clamp ([minValue...maxValue] -> [1...0])
    const mask = Nodes.math("/", Nodes.math("-", vTransmission, transmissionMax), transmissionMin - transmissionMax)
    const clampedMask = Nodes.math("max", Nodes.math("min", mask, 1), 0)
    return clampedMask
}

export function alphaFromMaskOrTransmission(mapsNodes: InputMapsNodes, mapsInfo: InputMapsInfo): Nodes.ImageNode | null {
    if (mapsNodes[TextureType.Mask]) return mapsNodes[TextureType.Mask]

    const transmission = mapsNodes[TextureType.Transmission]
    if (!transmission) return null

    const transmissionInfo = mapsInfo[TextureType.Transmission].data.type === "DataObject" ? mapsInfo[TextureType.Transmission].data : undefined
    const transmissionMax = transmissionInfo?.transmissionMax ?? 1
    const transmissionMin = transmissionInfo?.transmissionMin ?? 0
    return convertTransmissionToMask(transmission, transmissionMin, transmissionMax)
}

export function ensureTargetSizeForImageMaps(
    mapsInfo: InputMapsInfo,
    imageMapsInfo: ImageMapInfo[],
    mapsNodes: InputMapsNodes,
    targetWidthPx: number,
    targetHeightPx: number,
) {
    for (const imageMapInfo of imageMapsInfo) {
        if (targetWidthPx !== imageMapInfo.width || targetHeightPx !== imageMapInfo.height)
            mapsNodes[imageMapInfo.textureType] = Nodes.resize(mapsNodes[imageMapInfo.textureType], targetWidthPx, targetHeightPx)
    }

    for (const type of Object.keys(mapsInfo)) {
        const mapInfo = mapsInfo[type]
        if (mapInfo.data.type === "ShaderNodeRGB") {
            mapsNodes[type] = Nodes.resize(mapsNodes[type], targetWidthPx, targetHeightPx)
        }
    }
}

export function convertSpecularStrengthToIor(specularStrength: Nodes.ImageNode): Nodes.ImageNode {
    const t = Nodes.math("sqrt", Nodes.math("*", Nodes.math("max", specularStrength, 0), 0.08))
    const numerator = Nodes.math("+", t, 1)
    const denominator = Nodes.math("+", Nodes.math("*", t, -1), 1)
    return Nodes.math("/", numerator, denominator)
}
